import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #pauseAndResetVideos() {
    const { dialog } = this.refs;
    const videos = dialog.querySelectorAll('video');

    videos?.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });
  }

  #playVideos() {
    const { dialog } = this.refs;
    const videos = dialog.querySelectorAll('video');

    videos?.forEach((video) => {
      const lazySources = video.querySelectorAll('source[data-src]');
      if (lazySources.length > 0) {
        lazySources.forEach((source) => {
          const dataSrc = source.getAttribute('data-src');
          if (dataSrc) {
            source.setAttribute('src', dataSrc);
            source.removeAttribute('data-src');
          }
        });
        video.load();
      }

      const tryPlay = () => {
        const playPromise = video.play?.();
        if (typeof playPromise?.catch === 'function') {
          playPromise.catch(() => {});
        }
      };

      if (video.readyState >= 2) {
        tryPlay();
      } else {
        video.addEventListener('canplay', tryPlay, { once: true });
        video.addEventListener('loadedmetadata', tryPlay, { once: true });
      }
    });
  }

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // If another dialog already locked the body (position:fixed; top:-Y),
    // window.scrollY reads 0. Recover the real scroll from body.style.top
    // so this dialog's close restores to the correct position.
    const lockedTop = document.body.style.top;
    const scrollY = lockedTop ? -parseInt(lockedTop, 10) || 0 : window.scrollY;
    this.#previousScrollY = scrollY;

    // Run synchronously (not inside requestAnimationFrame) so showModal() and the
    // autofocus below happen within the originating user gesture. Mobile browsers
    // (notably iOS Safari) only open the on-screen keyboard for a programmatic
    // input.focus() when it runs in the same call stack as the tap — deferring to
    // rAF loses the gesture and the field appears unfocused until tapped again.
    // The reads above (scrollY) already completed, so no read/write thrash here.
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;

    dialog.showModal();

    // Opt-in: when `data-autofocus` holds a selector, move focus to that element
    // (e.g. the search input) so the user can type immediately. Otherwise redirect
    // focus to the dialog itself: native showModal() auto-focuses the first focusable
    // child and paints a :focus-visible ring even on pointer/touch opens — focusing
    // the non-interactive dialog element avoids that ring. Keyboard users still get a
    // ring when they Tab to a control, and screen readers announce via aria-label(ledby).
    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');

    const autofocusSelector = this.getAttribute('data-autofocus');
    const autofocusTarget = autofocusSelector ? dialog.querySelector(autofocusSelector) : null;

    if (autofocusTarget instanceof HTMLElement) {
      autofocusTarget.focus({ preventScroll: true });
    } else {
      dialog.focus({ preventScroll: true });
    }

    this.dispatchEvent(new DialogOpenEvent());

    // Defer OUT of the originating tap/click gesture. Two reasons:
    // 1. The trigger button lives inside <dialog-component> but OUTSIDE <dialog>.
    //    If we attach #handleClick synchronously, the very click that opened the
    //    dialog keeps bubbling up to this element, isClickedOutside() reports true
    //    (the button is outside <dialog>), and the dialog closes immediately.
    // 2. iOS Safari forces a non-`playsinline` video into native fullscreen only
    //    when play() runs inside the user gesture; running it in rAF keeps the
    //    video inline in the modal. Desktop autoplays regardless.
    requestAnimationFrame(() => {
      this.#playVideos();
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.#pauseAndResetVideos();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
